import { useState, useEffect } from 'react';
import back_app from '../../lib/back_app';

interface Props {
  data: any;
  onChange: (field: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

type CreatorTier = 'small' | 'medium' | 'large';

export default function CampaignBudget({ data, onChange, onNext, onBack }: Props) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [account, set_account] = useState<any>(null);
  const [account_loading, set_account_loading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await back_app.bprofile_account_get();
        if (!res?.err)
          set_account(res?.account || null);
      } catch (e) {
        console.error('account fetch failed', e);
      }
      set_account_loading(false);
    })();
  }, []);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!data.budget_info.total_budget || data.budget_info.total_budget <= 0) {
      newErrors.total_budget = 'Total budget must be greater than 0';
    }

    
    const hasEnabledTier = 
      data.budget_info.creator_tiers?.small?.enabled ||
      data.budget_info.creator_tiers?.medium?.enabled ||
      data.budget_info.creator_tiers?.large?.enabled;

    if (!hasEnabledTier) {
      newErrors.creator_tiers = 'Select at least one creator tier';
    }

    
    if (data.budget_info.creator_tiers?.small?.enabled) {
      if (!data.budget_info.creator_tiers.small.payment || data.budget_info.creator_tiers.small.payment <= 0) {
        newErrors.small_payment = 'Small creator payment must be greater than 0';
      }
    }

    if (data.budget_info.creator_tiers?.medium?.enabled) {
      if (!data.budget_info.creator_tiers.medium.payment || data.budget_info.creator_tiers.medium.payment <= 0) {
        newErrors.medium_payment = 'Medium creator payment must be greater than 0';
      }
    }

    if (data.budget_info.creator_tiers?.large?.enabled) {
      if (!data.budget_info.creator_tiers.large.payment || data.budget_info.creator_tiers.large.payment <= 0) {
        newErrors.large_payment = 'Large creator payment must be greater than 0';
      }
    }

    if (!data.schedule.is_always_on) {
      if (!data.schedule.start_date) {
        newErrors.start_date = 'Start date is required';
      }
      if (!data.schedule.end_date) {
        newErrors.end_date = 'End date is required';
      }
      if (data.schedule.start_date && data.schedule.end_date) {
        if (new Date(data.schedule.start_date) >= new Date(data.schedule.end_date)) {
          newErrors.end_date = 'End date must be after start date';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      
      onChange('budget_info', {
        ...data.budget_info,
        remaining: data.budget_info.total_budget,
        spent: 0,
      });
      onNext();
    }
  };

  const handleBudgetChange = (field: string, value: number) => {
    onChange('budget_info', {
      ...data.budget_info,
      [field]: value,
    });
  };

  const handleTierToggle = (tier: CreatorTier) => {
    const currentTiers = data.budget_info.creator_tiers || {
      small: { enabled: false, payment: 0 },
      medium: { enabled: false, payment: 0 },
      large: { enabled: false, payment: 0 }
    };

    onChange('budget_info', {
      ...data.budget_info,
      creator_tiers: {
        ...currentTiers,
        [tier]: {
          ...currentTiers[tier],
          enabled: !currentTiers[tier]?.enabled
        }
      }
    });
  };

  const handleTierPayment = (tier: CreatorTier, payment: number) => {
    const currentTiers = data.budget_info.creator_tiers || {
      small: { enabled: false, payment: 0 },
      medium: { enabled: false, payment: 0 },
      large: { enabled: false, payment: 0 }
    };

    onChange('budget_info', {
      ...data.budget_info,
      creator_tiers: {
        ...currentTiers,
        [tier]: {
          ...currentTiers[tier],
          payment: payment
        }
      }
    });
  };

  const handleScheduleChange = (field: string, value: any) => {
    onChange('schedule', {
      ...data.schedule,
      [field]: value,
    });
  };

  
  if (!data.budget_info.creator_tiers) {
    onChange('budget_info', {
      ...data.budget_info,
      creator_tiers: {
        small: { enabled: true, payment: 100 },
        medium: { enabled: true, payment: 300 },
        large: { enabled: true, payment: 1000 }
      }
    });
  }

  const tiers = data.budget_info.creator_tiers || {
    small: { enabled: false, payment: 0 },
    medium: { enabled: false, payment: 0 },
    large: { enabled: false, payment: 0 }
  };

  
  const estimatedCreators = {
    small: tiers.small.enabled && tiers.small.payment > 0 
      ? Math.floor(data.budget_info.total_budget / tiers.small.payment)
      : 0,
    medium: tiers.medium.enabled && tiers.medium.payment > 0
      ? Math.floor(data.budget_info.total_budget / tiers.medium.payment)
      : 0,
    large: tiers.large.enabled && tiers.large.payment > 0
      ? Math.floor(data.budget_info.total_budget / tiers.large.payment)
      : 0
  };

  const tierDefinitions = {
    small: {
      name: 'Small Creators',
      icon: '🌱',
      description: '1K - 10K followers',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-700',
      recommended: 100
    },
    medium: {
      name: 'Medium Creators',
      icon: '📈',
      description: '10K - 100K followers',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700',
      recommended: 300
    },
    large: {
      name: 'Large Creators',
      icon: '⭐',
      description: '100K+ followers',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-700',
      recommended: 1000
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Budget & Payments
        </h3>
        <p className="text-gray-600">
          Set your campaign budget and creator payments
        </p>
      </div>

      {}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-gray-900 flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
              </svg>
            </div>
            Brand Account
          </h4>
          <a
            href="/brand/profile"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline font-medium"
          >
            Manage →
          </a>
        </div>
        {account_loading ? (
          <div className="text-sm text-gray-500">Loading account…</div>
        ) : !account ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
            You don't have an account yet.{' '}
            <a href="/brand/profile" className="underline font-semibold">
              Create one in your profile
            </a>{' '}
            so the budget can be funded when this campaign starts.
          </div>
        ) : (
          <div>
            <div className="text-sm text-gray-600">Available balance</div>
            <div className="text-3xl font-bold text-gray-900">
              ${(account.balance || 0).toLocaleString(undefined,
                {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>
            {!!account.pending_out && (
              <div className="text-xs text-gray-500 mt-1">
                ${account.pending_out.toLocaleString()} held in pending transfers
              </div>
            )}
            {!!data.budget_info.total_budget
              && data.budget_info.total_budget > (account.balance || 0) && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                Budget exceeds balance by{' '}
                <span className="font-bold">
                  ${(data.budget_info.total_budget
                    - (account.balance || 0)).toLocaleString()}
                </span>
                .{' '}
                <a href="/brand/profile" className="underline font-semibold">
                  Add money
                </a>{' '}
                before launching, or the campaign won't be funded.
              </div>
            )}
            {!!data.budget_info.total_budget
              && data.budget_info.total_budget <= (account.balance || 0) && (
              <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                ✓ Enough funds to launch. ${data.budget_info.total_budget.toLocaleString()}{' '}
                will be moved to a dedicated campaign account when activated.
              </div>
            )}
          </div>
        )}
      </div>

      {}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-4 sm:p-6">
        <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
            </svg>
          </div>
          Total Campaign Budget
        </h4>

        <div className="max-w-md">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Total Budget <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">
              $
            </span>
            <input
              type="number"
              value={data.budget_info.total_budget || ''}
              onChange={(e) => handleBudgetChange('total_budget', parseFloat(e.target.value) || 0)}
              placeholder="10000"
              min="0"
              step="100"
              className={`w-full pl-8 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold ${
                errors.total_budget ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.total_budget && (
            <p className="text-red-500 text-sm mt-1">{errors.total_budget}</p>
          )}
        </div>
      </div>

      {}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6">
        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
            </svg>
          </div>
          Creator Payment Tiers
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          Select which creator tiers you want to work with and set payment per participation
        </p>

        {errors.creator_tiers && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm font-semibold">{errors.creator_tiers}</p>
          </div>
        )}

        <div className="space-y-4">
          {(Object.keys(tierDefinitions) as CreatorTier[]).map((tier) => {
            const def = tierDefinitions[tier];
            const isEnabled = tiers[tier]?.enabled || false;
            const payment = tiers[tier]?.payment || 0;
            const estimated = estimatedCreators[tier];

            return (
              <div
                key={tier}
                className={`border-2 rounded-xl overflow-hidden transition-all ${
                  isEnabled 
                    ? `${def.borderColor} bg-white shadow-md` 
                    : 'border-gray-200 bg-gray-50 opacity-60'
                }`}
              >
                {}
                <div className={`p-4 ${isEnabled ? def.bgColor : 'bg-gray-100'}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={() => handleTierToggle(tier)}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex-1 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{def.icon}</span>
                        <div>
                          <div className="font-bold text-gray-900">{def.name}</div>
                          <div className="text-sm text-gray-600">{def.description}</div>
                        </div>
                      </div>
                      {isEnabled && estimated > 0 && (
                        <div className="text-right">
                          <div className="text-xs text-gray-600">Estimated</div>
                          <div className={`text-2xl font-bold ${def.textColor}`}>
                            ~{estimated}
                          </div>
                          <div className="text-xs text-gray-600">creators</div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {}
                {isEnabled && (
                  <div className="p-4 border-t">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Payment per Creator <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3 items-start">
                      <div className="flex-1 relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                          $
                        </span>
                        <input
                          type="number"
                          value={payment || ''}
                          onChange={(e) => handleTierPayment(tier, parseFloat(e.target.value) || 0)}
                          placeholder={def.recommended.toString()}
                          min="0"
                          step="10"
                          className={`w-full pl-8 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-semibold ${
                            errors[`${tier}_payment`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                      </div>
                      <div className={`px-4 py-3 ${def.bgColor} ${def.borderColor} border-2 rounded-lg`}>
                        <div className="text-xs text-gray-600 whitespace-nowrap">Recommended</div>
                        <div className={`font-bold ${def.textColor}`}>${def.recommended}</div>
                      </div>
                    </div>
                    {errors[`${tier}_payment`] && (
                      <p className="text-red-500 text-sm mt-1">{errors[`${tier}_payment`]}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6">
        <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
            </svg>
          </div>
          Campaign Schedule
        </h4>

        {}
        <div className="mb-6">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative">
              <input
                type="checkbox"
                checked={data.schedule.is_always_on}
                onChange={(e) => handleScheduleChange('is_always_on', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-blue-500 peer-checked:to-purple-500"></div>
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                Always Active
              </div>
              <div className="text-sm text-gray-600">
                Campaign runs continuously until you pause it
              </div>
            </div>
          </label>
        </div>

        {}
        {!data.schedule.is_always_on && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
            {}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={data.schedule.start_date}
                onChange={(e) => handleScheduleChange('start_date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.start_date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.start_date && (
                <p className="text-red-500 text-sm mt-1">{errors.start_date}</p>
              )}
            </div>

            {}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={data.schedule.end_date}
                onChange={(e) => handleScheduleChange('end_date', e.target.value)}
                min={data.schedule.start_date || new Date().toISOString().split('T')[0]}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.end_date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.end_date && (
                <p className="text-red-500 text-sm mt-1">{errors.end_date}</p>
              )}
            </div>
          </div>
        )}

        {data.schedule.is_always_on && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              ✓ Your campaign will start immediately after launch and run continuously
            </p>
          </div>
        )}
      </div>

      {}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 mb-1">Payment Model</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• One-time payment per creator participation</li>
              <li>• Choose which creator tiers fit your campaign goals</li>
              <li>• Larger creators typically deliver more reach and engagement</li>
              <li>• Small creators often have higher engagement rates</li>
            </ul>
          </div>
        </div>
      </div>

      {}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
        <button
          type="button"
          onClick={onBack}
          className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
        >
          Continue to Review →
        </button>
      </div>
    </div>
  );
}